import FTUEService from './FTUEService';
import StudentInformationService from './StudentInformationService';
import { getUserData } from './API';
import { logSessionInfo } from './logger';
import { isiPad } from './environment';
import { shouldUseLocalPlacementAsr, resolveLocalAsrPlacementLocale } from './asr/localAsrEnablement';
import { getLocalAsrBackendId, LOCAL_ASR_BACKEND } from './asr/asrBackend';
import {
  BAD_SIGNAL_NETWORK_ENABLED,
  BAD_SIGNAL_REQUIRES_DISTRICT_THRESHOLD,
  HIGH_PRECISION_BRANCHES,
  getBadSignalKillSwitches,
  getSignalThresholds,
  isBadSignalCursorEnabled,
  isBadSignalForcedSession,
  isBadSignalLogOnly,
} from './cursor/signalThresholds';
import { getPriorActivitySignals } from './cursor/priorActivitySignals';
import { resolveBadSignalBehavior } from './cursor/badSignalCursorPolicy';
import { CURSOR_BEHAVIOR } from './cursor/cursorBehavior';
import { FORCE_TAP_TO_READ, FORCE_CURSOR_FREE, FORCE_LOCAL_ASR_WARMUP } from '../constants/featureFlags';

/**
 * CursorOrchestrator - source of truth for the reader's session-level UX decisions:
 *
 * 1. ASR routing: local (on-device) placement ASR vs server ASR. Two inputs, both
 *    owned here: static eligibility (locale/device/license via localAsrEnablement)
 *    and the session's measured warmup verdict (TT-1169), which asr/localAsrWarmup
 *    pushes in once the capability run settles. shouldUseLocalAsr() is the one question
 *    consumers ask; a device measured too slow answers "server" everywhere at once
 *    — phrase routing, ASR-connection waits, placement_manager labeling, and story
 *    graph downloads.
 * 2. Cursor UX behavior: which cursor treatment the student gets (standard cursor,
 *    cursor-free, and future behavior types), including each behavior's FTUE.
 *
 * ReaderManager owns the cursor lifecycle: it calls initCursorSession() on mount and
 * setStoryContext() whenever a story loads or changes. Everything else just asks.
 * The warmup route decision follows the ASR runtime's lifecycle instead — see the
 * note on localAsrWarmupRouteDecision.
 */

// Defined in cursor/cursorBehavior so the policy module can name the behaviors without importing
// this one; re-exported here because every consumer reads them off the orchestrator.
export { CURSOR_BEHAVIOR };

export const CURSOR_FTUE_VARIANTS = Object.freeze({
  STANDARD: 'standard',
  POST_ASSESSMENT: 'post-assessment',
});

// behavior -> FTUE id persisted via FTUEService; behaviors without an entry have no FTUE
const BEHAVIOR_FTUE_IDS = Object.freeze({
  [CURSOR_BEHAVIOR.CURSOR_FREE]: 'cursor-free-tutor',
  [CURSOR_BEHAVIOR.TAP_TO_READ]: 'tap-to-read-tutor',
});

// behavior -> the `mode` the bad-signal branch persists in SIS metadata.badSignalAltCursorMode
// ({firstActivityId, mode} | null, GT-326) when it PUTS a student in that mode. A dedicated field
// rather than an ftueStatuses entry: it is not an FTUE, a CSM exits the student by setting it to
// null instead of read-modify-writing the whole array, and analytics can query it directly.
// The strings are the stored contract — a value outside this map recalls nothing, and is never
// overwritten (see outranksPersistedBehavior).
const BAD_SIGNAL_ALT_CURSOR_MODES = Object.freeze({
  [CURSOR_BEHAVIOR.CURSOR_FREE]: 'bad-signal-cursor-free',
  [CURSOR_BEHAVIOR.TAP_TO_READ]: 'bad-signal-tap-to-read',
});

// behavior -> FTUE id recording that a student the bad-signal branch placed has SEEN that mode's
// onboarding. The branch's placements never write BEHAVIOR_FTUE_IDS: `cursor-free-tutor` is what
// the legacy rules read as "keep this student in cursor-free regardless of WCPM" (bypassWcpmCheck),
// so writing it would keep a bad-signal student in cursor-free through the legacy path after
// badSignalAltCursorMode was cleared -- the documented manual exit would be a no-op. With their own
// id, the exit is: set badSignalAltCursorMode to null. These ids only stop a re-placed student
// from seeing the interstitial twice.
const BAD_SIGNAL_ONBOARDING_FTUE_IDS = Object.freeze({
  [CURSOR_BEHAVIOR.CURSOR_FREE]: 'bad-signal-cursor-free-ftue',
  [CURSOR_BEHAVIOR.TAP_TO_READ]: 'bad-signal-tap-to-read-ftue',
});

// Rank for replacing a persisted mode, highest first. The field holds one mode, so a fresh
// placement over an existing one (possible only when the stored mode's gate has since closed —
// e.g. a tap-to-read student now on a Chromebook) needs a rule: cursor-free replaces tap-to-read,
// never the reverse, because leaving a student who has since grown past the pivot in tap-to-read
// is the worse mistake. A tap-to-read placement over a gated-off cursor-free latch holds for the
// page load only.
const BAD_SIGNAL_BEHAVIOR_PRECEDENCE = Object.freeze([
  CURSOR_BEHAVIOR.CURSOR_FREE,
  CURSOR_BEHAVIOR.TAP_TO_READ,
]);

/**
 * How the branch arrived at this activity's decision. The dark launch's second question — not
 * "who is in the mode" but "who was PUT there today versus who was already in it" — is only
 * answerable if the log can tell a fresh decision from a recalled one, and by the time
 * computeBadSignalBehavior returns, a behavior read off badSignalAltCursorMode is indistinguishable
 * from one just resolved from signals.
 *
 * SESSION and LATCHED are separate because they answer different things: SESSION is this page
 * load re-asking on the 2nd+ activity, LATCHED is the student walking in already in the mode
 * from an earlier day.
 *
 * A dark launch persists nothing, so it reports only NEW and SESSION; a student the branch keeps
 * choosing shows up as NEW on separate days rather than as LATCHED, which is the stronger evidence
 * anyway — the branch re-deciding independently, not a latch echoing its own earlier write. LATCHED
 * stays readable so a district rolled BACK from live to dark still reports its existing latches
 * honestly rather than as fresh decisions.
 */
export const BAD_SIGNAL_DECISION_SOURCE = Object.freeze({
  NEW: 'new',          // resolved from this activity's prior-activity signals
  SESSION: 'session',  // already decided earlier in this page load
  LATCHED: 'latched',  // SIS metadata.badSignalAltCursorMode, written by an earlier session
});

/**
 * Why computeEligibleBehavior returned before the bad-signal branch could run. Recorded into the
 * dark-launch log's `gate` so a `decisionEvaluated: false` row says WHICH earlier gate answered
 * rather than being an undiagnosable hole in the measurement -- early reader, for one, determines
 * which slice of the population the dataset can speak for. The values are stable log tokens; a
 * query filtering on them and the producer must agree on spelling.
 */
export const BAD_SIGNAL_SKIP_REASON = Object.freeze({
  NOT_TUTOR: 'not_tutor',                     // not on the /student/tutor path
  EARLY_READER: 'early_reader',               // isEarlyReader / isSpanishEarlyReader
  FORCE_TAP_TO_READ: 'force_tap_to_read',     // ?forceTapToRead=true (dev/QA override)
  FORCE_CURSOR_FREE: 'force_cursor_free',     // ?forceCursorFree=true
  FORCE_CURSOR_FREE_OFF: 'force_cursor_free_off', // ?forceCursorFree=false
});

const DEFAULT_STORY_CONTEXT = Object.freeze({ isERSS: false });

// Lowest grade that skips the cursor-free onboarding ramp (TT-1238)
const CURSOR_FREE_GRADE_BAND_MIN = 5;

function createInitialState() {
  return {
    initialized: false,
    eligibleBehavior: CURSOR_BEHAVIOR.STANDARD, // user-level answer, computed once per session
    shouldShowFTUE: false,                      // decided at session start, consumed at story launch
    ftueVariant: CURSOR_FTUE_VARIANTS.STANDARD,
    storyContext: { ...DEFAULT_STORY_CONTEXT },
  };
}

let state = createInitialState();

// Bad-signal mode latched for THIS page load. Outside `state` so the per-activity initCursorSession
// cannot recompute it away; duplicates the persisted field, which a mid-session saveUserData can drop.
let badSignalSessionBehavior = null;

// A live placement decided at story fetch and not yet persisted (GT-326). The decision runs in
// getStory, BEFORE the activity exists, and the stored record names the first activity read in the
// mode -- so the write waits for persistBadSignalPlacement() at launch, once setSessionActivityId has
// run. Held until a launch with an activity id consumes it: a failed createActivity leaves it
// pending, and the next launch in the page load (the actual first activity in the mode) writes it.
let badSignalPendingPlacement = null;

// The last decision the bad-signal branch made, {behavior, source, steps}, for ReaderManager to
// log. Held rather than returned because in log-only mode computeEligibleBehavior discards the
// behavior, and the log is then the only place the decision exists at all. Carries `error` instead
// of steps when the branch threw -- the story still launched on the legacy answer, and the row
// says why there is no decision.
let badSignalDecision = null;

// Why the bad-signal branch did not evaluate on the last computeEligibleBehavior run (a
// BAD_SIGNAL_SKIP_REASON value), or null when it did evaluate. One of the early returns above the
// branch can short-circuit before it runs; the dark-launch log records this so a
// `decisionEvaluated: false` row names the gate that answered instead of being a hole.
let badSignalNotEvaluatedReason = null;

// The bad-signal behavior computeEligibleBehavior actually RETURNED on its last run, or null. This
// is provenance for the FTUE coordination below: only a placement the branch applied uses the
// bad-signal onboarding flag. A log-only decision is withheld and so never sets this, which keeps
// the legacy onboarding write untouched for a student the legacy rules moved on their own.
let badSignalAppliedBehavior = null;

// Onboardings completed in THIS page load for bad-signal placements, whether or not the flag was
// persisted. A forced test session skips every write and would otherwise re-show the interstitial
// on every later story of the session.
let badSignalOnboardingSeen = new Set();

// The userId the six latches above belong to. Every branch of endUserSession tears the page down
// (close tab / redirect / location.reload), so module state should never outlive one student -- but
// that is an invariant of a function in another module, not one asserted here, and on a shared
// classroom iPad the failure it guards is student B inheriting student A's latched mode and their
// completed onboarding. Asserted rather than inherited: cheaper than the incident.
let badSignalLatchedUserId = null;

// ============ Session lifecycle ============

// Drops every per-page-load bad-signal latch when the student behind them changes. The session
// recall the dark-launch log reports as `source: session` is deliberately per page load, so this
// must key on the user and NOT on the activity: clearing per activity would erase the recall the
// log measures.
function resetBadSignalLatchesOnUserChange() {
  const userId = getUserData()?.userId ?? null;
  if (userId === badSignalLatchedUserId) {
    return;
  }
  badSignalLatchedUserId = userId;
  badSignalSessionBehavior = null;
  badSignalPendingPlacement = null;
  badSignalDecision = null;
  badSignalNotEvaluatedReason = null;
  badSignalAppliedBehavior = null;
  badSignalOnboardingSeen = new Set();
}

/**
 * Computes the session's user-level cursor behavior eligibility and FTUE state.
 *
 * ReaderManager calls it once per mount with `{ badSignal: false }`, before any story exists, and
 * then per tutor activity through refreshCursorSessionForActivity. The mount-time call skips the
 * bad-signal branch (TT-991) on purpose: the branch answers "what should THIS activity's cursor
 * be", and a decision taken with no activity to attach it to would latch the session and make the
 * first real activity read as a `session` recall -- with the classification that motivated it
 * missing from the one row the dark launch most needs.
 *
 * @param {{badSignal?: boolean}} [options] badSignal=false runs the legacy rules only
 */
export function initCursorSession({ badSignal = true } = {}) {
  resetBadSignalLatchesOnUserChange();
  const eligibleBehavior = computeEligibleBehavior({ badSignal });
  const ftueRequired =
    (eligibleBehavior === CURSOR_BEHAVIOR.CURSOR_FREE ||
     eligibleBehavior === CURSOR_BEHAVIOR.TAP_TO_READ) &&
    !hasCursorBehaviorFTUECompleted(eligibleBehavior);
  
  state = {
    initialized: true,
    eligibleBehavior,
    shouldShowFTUE: ftueRequired,
    ftueVariant: eligibleBehavior === CURSOR_BEHAVIOR.CURSOR_FREE
      ? computeCursorFreeFTUEVariant()
      : CURSOR_FTUE_VARIANTS.STANDARD, // Tap-to-read uses standard variant
    storyContext: { ...DEFAULT_STORY_CONTEXT },
  };
}

/**
 * Provides story-level context for the cursor behavior decision.
 * ERSS stories always keep the cursor regardless of user-level eligibility.
 */
export function setStoryContext({ isERSS = false } = {}) {
  state.storyContext = { isERSS: Boolean(isERSS) };
}

/**
 * The per-tutor-activity re-decision (TT-991). ReaderManager stays mounted across activities, so
 * without this the 2nd+ tutor activity of a session would replay the 1st one's decision.
 *
 * Two different things depending on the rollout state, and the difference IS the dark launch:
 *  - live: a full initCursorSession(), so the branch's answer (and, by product decision -- Ka,
 *    2026-08-11 -- the legacy rules against the refreshed WCPM) reaches the next story;
 *  - log-only: computeEligibleBehavior() for its side effect only. The decision is recorded for
 *    the log and `state` is not touched, so the student's cursor, FTUE and variant stay exactly
 *    what the mount-time init computed -- which is what the pre-TT-991 app did. A dark launch that
 *    re-derived the legacy eligibility per activity would move students whose WCPM crossed the
 *    district threshold mid-session, a student-visible change reached through the legacy path
 *    rather than through the withheld branch.
 */
export function refreshCursorSessionForActivity() {
  if (isBadSignalLogOnly()) {
    // Asserted on both arms. The live arm gets it through initCursorSession; skipping it here
    // would leave the invariant half-asserted, which is the state the reset exists to end.
    resetBadSignalLatchesOnUserChange();
    computeEligibleBehavior();
    return;
  }
  initCursorSession();
}

export function resetCursorOrchestratorForTests() {
  state = createInitialState();
  badSignalLatchedUserId = null;
  badSignalSessionBehavior = null;
  badSignalPendingPlacement = null;
  badSignalDecision = null;
  badSignalNotEvaluatedReason = null;
  badSignalAppliedBehavior = null;
  badSignalOnboardingSeen = new Set();
  localAsrWarmupRouteDecision = null;
  localAsrWarmupDeadlineOverrun = false;
}

// ============ Cursor behavior ============

/**
 * The cursor UX behavior for the current story: user-level eligibility
 * (computed at initCursorSession) combined with story-level context.
 *
 * Story context normally comes from the last setStoryContext() push. Callers
 * that read during render must pass { isERSS } derived from current props:
 * React renders before componentDidUpdate, so the pushed context can be one
 * story behind on prop-driven story swaps. Event-time callers (timers, pause
 * handling, FTUE gating) run after the sync and can rely on module state.
 */
export function getCursorBehavior({ isERSS = state.storyContext.isERSS } = {}) {
  if (!state.initialized) {
    return CURSOR_BEHAVIOR.STANDARD; // fail-safe: cursor visible
  }
  if (isERSS) {
    return CURSOR_BEHAVIOR.STANDARD; // ERSS stories always keep the cursor
  }
  return state.eligibleBehavior;
}

export function isCursorFreeBehavior(storyContext) {
  return getCursorBehavior(storyContext) === CURSOR_BEHAVIOR.CURSOR_FREE;
}

export function isTapToReadBehavior(storyContext) {
  return getCursorBehavior(storyContext) === CURSOR_BEHAVIOR.TAP_TO_READ;
}

/**
 * Determines the user-level cursor behavior from the student's WCPM, activity
 * history, and district configuration. Story-level context (ERSS) is applied
 * separately in getCursorBehavior().
 *
 * District override flags (forceTapWordOff, forceCursorFreeOff) only affect their
 * target mode and do not prevent other modes from being checked.
 *
 * Both cursor-free and tap-to-read require:
 * - Tutor mode (URL check)
 * - Not early reader (user-level flag)
 *
 * Cursor-free on the LEGACY path requires:
 * - District threshold configured (feature enabled; 0/null/undefined disables)
 * - WCPM value available
 * - WCPM meets the district threshold (bypassed once the cursor-free FTUE is
 *   completed, so a temporary WCPM dip doesn't eject the student)
 * - At least 1 assessment completed, or more than 5 tutor stories read
 *
 * The last three are the onboarding ramp, and the grade band in
 * isCursorFreeGradeBandExempt waives all of them (TT-1238). The district threshold
 * is deliberately not waived: it doubles as the feature switch, so a district that
 * never turned cursor-free on gets nothing regardless of grade. That is why the
 * threshold lookup runs before the WCPM-availability check rather than after it —
 * both answer STANDARD on their own, so the order is behaviour-neutral for
 * everyone below the grade band.
 *
 * THE BAD-SIGNAL BRANCH IS THE ONE EXCEPTION, and it returns before any of the above: it can
 * answer CURSOR_FREE in a district with no threshold configured at all
 * (BAD_SIGNAL_REQUIRES_DISTRICT_THRESHOLD, waived 2026-09-16). Everything in this list still
 * describes the legacy path it falls through to.
 *
 * Tap-to-read has exactly one trigger (TT-991): the bad-signal branch, when the previous tutor
 * activity had bad audio, the device is an iPad, the student is at or below the WCPM pivot, and
 * forceTapWordOff is not set. There is no standalone license path -- the enableTapToReadTutor
 * grant (TT-1012) was removed because a license flag should never by itself put a student into
 * tap-to-read; the whole point of the mode is to respond to a bad signal. ?forceTapToRead=true is
 * a dev/QA override only.
 *
 * The bad-signal branch (TT-991) is gated off by default and is the sole producer of tap-to-read
 * (and, above the pivot, of the waived-ramp cursor-free): it answers from the previous tutor
 * activity's signals. During its dark launch (isBadSignalLogOnly) the branch still runs but writes
 * nothing per-student and its answer is not returned — so this function's result is the pre-TT-991
 * one, and getBadSignalDecision() holds the counterfactual for the session log.
 */
export function computeEligibleBehavior({ badSignal = true } = {}) {
  // Cleared up front, not only on the paths that set it: the early returns above the bad-signal
  // block would otherwise leave the previous activity's decision standing for this one to log.
  badSignalDecision = null;
  badSignalAppliedBehavior = null;
  badSignalNotEvaluatedReason = null;

  const userData = getUserData();

  // Must be in tutor mode for any non-standard behavior
  if (!window.location.pathname.startsWith('/student/tutor')) {
    badSignalNotEvaluatedReason = BAD_SIGNAL_SKIP_REASON.NOT_TUTOR;
    return CURSOR_BEHAVIOR.STANDARD;
  }

  // Check early reader status - applies to both cursor-free and tap-to-read
  const isEarlyReader = userData.isEarlyReader || userData.isSpanishEarlyReader;

  if (isEarlyReader) {
    badSignalNotEvaluatedReason = BAD_SIGNAL_SKIP_REASON.EARLY_READER;
    return CURSOR_BEHAVIOR.STANDARD;
  }
  
  // Dev/QA override: ?forceTapToRead=true forces tap-to-read on, bypassing the bad-signal gating
  // that is otherwise its only trigger. A debug tool, honoured on every stack; see FORCE_TAP_TO_READ.
  // (There is no standalone license path any more -- TT-1012's enableTapToReadTutor grant was
  // removed so a license can never by itself place a student in tap-to-read.)
  if (FORCE_TAP_TO_READ) {
    badSignalNotEvaluatedReason = BAD_SIGNAL_SKIP_REASON.FORCE_TAP_TO_READ;
    return CURSOR_BEHAVIOR.TAP_TO_READ;
  }

  // Cursor free check starts here
  // Dev/QA override: ?forceCursorFree=true|false forces cursor-free mode on or
  // off. `true` overrides the district threshold / WCPM / onboarding-ramp gating below; `false`
  // forces STANDARD (cursor kept). Mirrors FORCE_TAP_TO_READ, and like it sits after the
  // early-reader and tap-to-read checks, so it does not override those. undefined (param absent)
  // falls through to the normal gating.
  if (FORCE_CURSOR_FREE === true) {
    badSignalNotEvaluatedReason = BAD_SIGNAL_SKIP_REASON.FORCE_CURSOR_FREE;
    return CURSOR_BEHAVIOR.CURSOR_FREE;
  }
  if (FORCE_CURSOR_FREE === false) {
    badSignalNotEvaluatedReason = BAD_SIGNAL_SKIP_REASON.FORCE_CURSOR_FREE_OFF;
    return CURSOR_BEHAVIOR.STANDARD;
  }

  // Bad-signal check (TT-991). Position is the rule, and it is bounded on both sides:
  //
  // BELOW the tap-to-read license block, so the branch can only fire where it changes something.
  // BELOW both dev/QA overrides — FORCE_TAP_TO_READ above and the FORCE_CURSOR_FREE pair directly
  // above — because a debug override a product branch can silently beat is worse than no override:
  // a dev sets ?forceCursorFree=true, lands in bad-signal tap-to-read, and concludes the param is
  // broken. Both values of that param outrank this branch, `false` ("cursor kept") included.
  // ABOVE the cursor-free district gates, because waiving that ramp is the TT-991 AC.
  //
  // Contained: this sits on the story-launch path for every enabled district, dark ones included,
  // and a throw here (a flag store that is down, a malformed stored payload) would reject the
  // story load. The branch has no opinion when it fails, and the row says so.
  if (badSignal && isBadSignalCursorEnabled(userData)) {
    let badSignalBehavior = null;
    try {
      badSignalBehavior = computeBadSignalBehavior(userData);
    } catch (err) {
      badSignalDecision = { behavior: null, source: null, steps: null, error: String(err?.message ?? err) };
      console.warn('[CursorOrchestrator] bad-signal branch failed; falling through', err);
    }
    // The dark launch is this one condition. Everything above still ran — signals classified,
    // policy applied, decision recorded for the log — and only the RETURN is withheld,
    // so a log-only session falls through to exactly the behavior it would have had before TT-991.
    // Any new consequence of the decision belongs above this line only if it is safe to ship dark.
    if (badSignalBehavior && !isBadSignalLogOnly()) {
      badSignalAppliedBehavior = badSignalBehavior;
      return badSignalBehavior;
    }
  }

  // District override: forceCursorFreeOff on appSettings disables cursor-free entirely
  if (userData.appSettings?.forceCursorFreeOff === true) {
    return CURSOR_BEHAVIOR.STANDARD;
  }

  // Get district-specific threshold, or fall back to global app settings (district 9999999)
  const districtThreshold = userData.cursorFreeTutorWCPMThreshold ??
                            userData.appSettings?.cursorFreeTutorWCPMThreshold;

  // Feature explicitly disabled (value 0 OR null OR undefined)
  if (!districtThreshold) {
    return CURSOR_BEHAVIOR.STANDARD;
  }

  // Upper grades skip the onboarding ramp entirely (TT-1238)
  if (isCursorFreeGradeBandExempt(userData)) {
    return CURSOR_BEHAVIOR.CURSOR_FREE;
  }

  const studentWcpm = userData.WCPM;

  // If student WCPM is not available, keep the cursor
  if (studentWcpm === undefined || studentWcpm === null) {
    return CURSOR_BEHAVIOR.STANDARD;
  }

  // Special case: if student has completed cursor-free FTUE, bypass WCPM threshold check
  // This keeps students in cursor-free mode once they've experienced it, even if WCPM temporarily drops.
  // The LEGACY flag only: a bad-signal placement's onboarding flag (BAD_SIGNAL_ONBOARDING_FTUE_IDS)
  // must not keep a student here after badSignalAltCursorMode is nulled.
  const bypassWcpmCheck = FTUEService.isFTUECompleted(BEHAVIOR_FTUE_IDS[CURSOR_BEHAVIOR.CURSOR_FREE]) === true;

  // Student must meet WCPM threshold (unless bypassed by FTUE completion)
  if (!bypassWcpmCheck && studentWcpm < districtThreshold) {
    return CURSOR_BEHAVIOR.STANDARD;
  }

  // Get student activity metrics
  const metrics = userData.studentMetrics || { storiesRead: 0, testsTaken: 0 };
  const testsTaken = metrics.testsTaken || 0;
  const storiesRead = metrics.storiesRead || 0;

  // If student has completed at least 1 assessment, they're eligible
  if (testsTaken > 0) {
    return CURSOR_BEHAVIOR.CURSOR_FREE;
  }

  // If student has 0 assessments completed, they must have read more than 5 tutor stories
  return storiesRead > 5 ? CURSOR_BEHAVIOR.CURSOR_FREE : CURSOR_BEHAVIOR.STANDARD;
}

// District inputs read here ONCE, exactly as the gates below read them, or the branch hands back a
// mode the gate three lines later would refuse. wcpmPivot must be passed or the override is dead.
//
// EVERY run classifies. Recall (the session latch, then badSignalAltCursorMode) decides which BEHAVIOR
// is returned, but the previous activity's signals are normalized, classified and put through the
// policy regardless, and all of it is recorded. The alternative -- skipping the measurement when
// recall answered -- left the 2nd+ activity of every session where the branch fired with no
// classification in its row, and those are the rows the dark launch exists to collect. The
// policy's own answer for THIS activity's signals is recorded beside the recalled one, so a row
// can say "still latched, and the current signal would (not) have re-fired".
function computeBadSignalBehavior(userData) {
  const districtInputs = {
    isIpad: isiPad(),
    tapDisabled: userData.appSettings?.forceTapWordOff === true,
    cursorFreeDisabled: userData.appSettings?.forceCursorFreeOff === true,
    cursorFreeConfigured: Boolean(
      userData.cursorFreeTutorWCPMThreshold ?? userData.appSettings?.cursorFreeTutorWCPMThreshold
    ),
  };

  // audioBranches must be forwarded, not just the booleans: cursor-free is gated on WHICH branch
  // fired, so dropping it here leaves the outcome silently unreachable.
  const {
    hadBadAudio, hadBadNetwork, badCountVerdict, audioBranches, networkBranches, networkHistory, source, signal, stale,
  } = getPriorActivitySignals();
  const thresholds = getSignalThresholds(userData);
  // GT-306's per-signal kill switches feed the fresh answer only, never recall below, so they stop
  // new placements without releasing a student who is already latched.
  const killSwitches = getBadSignalKillSwitches(userData);
  const resolvedBehavior = resolveBadSignalBehavior({
    hadBadAudio,
    hadBadNetwork,
    audioBranches,
    wcpm: userData.WCPM,
    ...districtInputs,
    wcpmPivot: thresholds.wcpmPivot,
    audioSignalOff: killSwitches.audioOff,
    networkSignalOff: killSwitches.networkOff,
  });

  // The step that can answer before the policy is consulted. The stored record is logged even when
  // it did not win, because "latched into tap-to-read but since moved off the iPad" and "never
  // latched" produce the same behavior and are different students. Read before recall so a write
  // this run makes cannot show up as the state the run started from.
  const recall = {
    badSignalAltCursorMode: readPersistedAltCursorMode(userData),
  };
  const remembered = recallBadSignalBehavior(districtInputs, userData);

  const behavior = remembered.behavior || resolvedBehavior;
  const decisionSource = remembered.behavior ? remembered.source : BAD_SIGNAL_DECISION_SOURCE.NEW;
  if (!remembered.behavior && resolvedBehavior) {
    rememberBadSignalBehavior(resolvedBehavior, userData);
  }

  // The policy's own constants, recorded per row rather than assumed: each is a decision a later
  // release may flip — network went live with GT-306 on 2026-10-06, the district threshold is
  // waived as of 2026-09-16 — and a row is unreadable without knowing which version of the gate
  // chain produced it.
  const policyRules = {
    networkEnabled: BAD_SIGNAL_NETWORK_ENABLED,
    requiresDistrictThreshold: BAD_SIGNAL_REQUIRES_DISTRICT_THRESHOLD,
    highPrecisionBranches: HIGH_PRECISION_BRANCHES,
  };

  // One group per step, in the order the steps ran: what the previous activity measured (and when,
  // and whether it was too old to use), what those measurements were compared against, the
  // verdicts that came out, the memory that could have answered first, and the policy's own inputs
  // and answer. Reading the groups top to bottom is reading the pipeline, which is what makes a
  // row diagnosable without the source open beside it.
  return recordBadSignalDecision(behavior, decisionSource, {
    priorActivitySignals: {
      signalSource: source,
      measuredAt: signal?.measuredAt ?? null,
      // The SRS status of the stored prior activity, carried in the normalized envelope by
      // fromStoredActivity (null on the live path and when there is no stored row). The read
      // behind it spans every non-archived status, and 28% of real tutor rows on prod (2026-09) never reach
      // scored, so the analysis has to be able to tell a verdict drawn from a completed story
      // apart from one drawn from an abandoned or muted one.
      activityStatus: signal?.activityStatus ?? null,
      displayStatus: signal?.displayStatus ?? null,
      stale,
      audio: signal?.audio ?? null,
      network: signal?.network ?? null,
    },
    // GT-306: the login tutor history TT-1377's network rule was computed over — how many sessions
    // counted, how many were high-RTT, which calendar days — or null when no history was stashed.
    // Launch-only by design, so this group is identical on every activity of a session.
    networkHistory: networkHistory ?? null,
    // The values APPLIED, license overrides included. Logging the in-code defaults instead
    // would make every row recorded before a retune unreadable after it.
    thresholds: {
      audio: thresholds.audio,
      network: thresholds.network,
      wcpmPivot: thresholds.wcpmPivot,
      maxSignalAgeDays: thresholds.maxSignalAgeDays,
    },
    // hadBadNetwork is TT-1377's history verdict; badCountVerdict is TT-1257's single-activity
    // count against its floor, logged as the sanity check the brief asked for and read by no rule.
    classification: { hadBadAudio, hadBadNetwork, badCountVerdict, audioBranches, networkBranches },
    recall,
    policy: { studentWcpm: userData.WCPM, ...districtInputs, killSwitches, resolvedBehavior, rules: policyRules },
  });
}

// Pass-through with a side effect, so no caller can return a decision without recording it.
// `source` is dropped when there is no behavior: "no opinion" has no provenance to report.
//
// `steps` is every value the branch actually READ, grouped by the step that read it, held here
// rather than re-read at the log site. Two reads of the same config can disagree — the district
// switches move between activities and the thresholds are remotely overridable — and a log that
// disagrees with the decision it describes is worse than no log.
function recordBadSignalDecision(behavior, source, steps) {
  badSignalDecision = {
    behavior: behavior || null,
    source: behavior ? source : null,
    steps,
  };
  return behavior;
}

/**
 * The decision the branch reached for the current activity — {behavior, source, steps} — or null
 * when the branch did not run at all. ReaderManager logs it; nothing may act on it, because in
 * log-only mode this is a counterfactual and getCursorBehavior() is what the student actually got.
 */
export function getBadSignalDecision() {
  return badSignalDecision;
}

/**
 * Why the branch did not evaluate on the last run — a BAD_SIGNAL_SKIP_REASON value — or null when
 * it did. ReaderManager folds this into the dark-launch log's `gate` so a `decisionEvaluated: false`
 * row is diagnosable (which of the upstream gates answered) rather than an unexplained non-run.
 */
export function getBadSignalNotEvaluatedReason() {
  return badSignalNotEvaluatedReason;
}

// Remembering is the point: the input is the PREVIOUS activity's signals and the next activity is
// usually clean. Signals, WCPM and pivot are NOT re-derived; the district's switches are.
// Returns {behavior, source} rather than a behavior: which of the two memories answered is the
// dark launch's "already in the mode" measurement, and is lost the moment the caller unwraps it.
function recallBadSignalBehavior(districtInputs, userData) {
  if (badSignalSessionBehavior
    && isBadSignalBehaviorStillAllowed(badSignalSessionBehavior, districtInputs)) {
    return { behavior: badSignalSessionBehavior, source: BAD_SIGNAL_DECISION_SOURCE.SESSION };
  }

  const persisted = persistedBehavior(userData);
  if (persisted && isBadSignalBehaviorStillAllowed(persisted, districtInputs)) {
    return { behavior: persisted, source: BAD_SIGNAL_DECISION_SOURCE.LATCHED };
  }
  return { behavior: null, source: null };
}

// The stored record as SIS returned it, narrowed to the contract's two fields, or null. Anything that
// is not an object reads as null: the decision log records what recall actually saw.
function readPersistedAltCursorMode(userData) {
  const stored = userData?.badSignalAltCursorMode;
  if (!stored || typeof stored !== 'object') {
    return null;
  }
  return { firstActivityId: stored.firstActivityId ?? null, mode: stored.mode ?? null };
}

// CURSOR_BEHAVIOR for the stored mode, or null when nothing is stored or the string is not one this
// build writes. Unknown is "not latched", never a guess.
function persistedBehavior(userData) {
  const mode = readPersistedAltCursorMode(userData)?.mode;
  const match = Object.entries(BAD_SIGNAL_ALT_CURSOR_MODES).find(([, stored]) => stored === mode);
  return match ? match[0] : null;
}

// The same gates resolveBadSignalBehavior applies to a fresh decision; one added there belongs here
// in the same edit. iPad is re-checked, not remembered — tap-to-read needs a touch device.
function isBadSignalBehaviorStillAllowed(behavior, { isIpad, tapDisabled, cursorFreeDisabled, cursorFreeConfigured }) {
  if (behavior === CURSOR_BEHAVIOR.TAP_TO_READ) {
    return isIpad === true && tapDisabled !== true;
  }
  if (behavior === CURSOR_BEHAVIOR.CURSOR_FREE) {
    return cursorFreeDisabled !== true
      && (!BAD_SIGNAL_REQUIRES_DISTRICT_THRESHOLD || cursorFreeConfigured === true);
  }
  return false;
}

// Latches the mode for this page load and, when the placement is to outlive it, queues the write
// for persistBadSignalPlacement(). Nothing is written here: the activity the record names does not
// exist yet.
//
// The dark launch stops at the in-memory latch and queues NOTHING per-student. A placement persisted
// while log-only would outlive the measurement: the student would be in the mode the instant the
// switch flipped, on evidence nobody had reviewed yet, and going live would inherit a population
// it never decided on. The in-memory half stays because it is not a save — it is what makes the
// 2nd+ activity of one page load read the same answer, which the log reports as `session`.
function rememberBadSignalBehavior(behavior, userData) {
  badSignalSessionBehavior = behavior;

  if (isBadSignalLogOnly()) {
    return;
  }

  // A forced test session leaves no mark either. Same reasoning as log-only, different cause: the
  // modes are sticky with no automatic exit, so latching the student QA happened to be driving
  // would strand them until a CSM cleared it. The in-memory latch above still stands, so the mode
  // survives the session and is actually testable.
  if (isBadSignalForcedSession()) {
    return;
  }

  if (!outranksPersistedBehavior(behavior, userData)) {
    return;
  }
  badSignalPendingPlacement = behavior;
}

// Whether a fresh placement should replace what is stored. Over nothing stored it always wins;
// otherwise BAD_SIGNAL_BEHAVIOR_PRECEDENCE decides, and an equal mode is never rewritten -- that would
// move firstActivityId off the activity that actually started the mode. A mode this build does not
// know is left alone: it is most likely one a newer build wrote, and a cached older client cannot
// rank it, so overwriting it would erase that placement and its firstActivityId.
function outranksPersistedBehavior(behavior, userData) {
  if (!readPersistedAltCursorMode(userData)?.mode) {
    return true;
  }
  const persisted = persistedBehavior(userData);
  if (!persisted) {
    return false;
  }
  return BAD_SIGNAL_BEHAVIOR_PRECEDENCE.indexOf(behavior) < BAD_SIGNAL_BEHAVIOR_PRECEDENCE.indexOf(persisted);
}

/**
 * Persists a pending bad-signal placement as SIS metadata.badSignalAltCursorMode =
 * {firstActivityId, mode} (GT-326). ReaderManager calls it at story launch with the activity it just
 * created, because that is the first point the activity the student is about to read in the mode
 * exists -- the decision itself ran earlier, in getStory. Passed in rather than read off userData:
 * userData.activityId is never cleared, so a stale id would be indistinguishable from this one.
 *
 * A no-op unless the branch made a NEW live placement this page load that has not been written yet:
 * recalled modes, log-only and forced sessions, and a placement the stored mode outranks never
 * queue one. Log-only, a forced session and the badSignalCursorEnabled license flag are checked
 * again here, so a queued placement is dropped rather than written if one has closed since the
 * decision. The per-behavior gates (isBadSignalBehaviorStillAllowed) and the precedence check are
 * not re-run: their inputs are login-time values that cannot change within a page load, and a user
 * change clears the pending placement. With no activity id the placement stays pending for the
 * next launch.
 *
 * The in-memory record is updated before the request is sent (StudentInformationService sets
 * userData first), so the network trip only decides whether the mode survives to the next login.
 * Caught, not propagated: a failed write must not fail the story launch. It is logged as
 * cursor_bad_signal_placement_failed so dropped placements are countable during rollout.
 */
export function persistBadSignalPlacement(firstActivityId) {
  const behavior = badSignalPendingPlacement;
  if (!behavior) {
    return;
  }
  const userData = getUserData();
  if (isBadSignalLogOnly() || isBadSignalForcedSession() || !isBadSignalCursorEnabled(userData)) {
    badSignalPendingPlacement = null;
    return;
  }
  if (!firstActivityId) {
    return;
  }
  badSignalPendingPlacement = null;

  const value = { firstActivityId, mode: BAD_SIGNAL_ALT_CURSOR_MODES[behavior] };
  const warn = (err) => {
    console.warn(`[CursorOrchestrator] bad-signal placement not persisted (${value.mode})`, err);
    try {
      logSessionInfo('cursor_bad_signal_placement_failed', {
        ...value,
        error: String(err?.message ?? err),
      });
    } catch (logErr) {
      console.warn('[CursorOrchestrator] bad-signal placement failure not logged', logErr);
    }
  };
  try {
    Promise.resolve(StudentInformationService.setBadSignalAltCursorMode(userData.userId, value)).catch(warn);
  } catch (err) {
    warn(err);
  }
}

/**
 * Whether the student's grade band waives the cursor-free onboarding ramp — the WCPM
 * value, the WCPM threshold, and the activity-history requirement (TT-1238). Product
 * wants upper-grade students in cursor-free now, collecting real usage, rather than
 * waiting out a ramp built for younger readers.
 *
 * WAIVES THE RAMP ONLY. Every other rule still decides ahead of this one in
 * computeEligibleBehavior: tutor mode, early reader, tap-to-read precedence,
 * forceCursorFreeOff, and the district threshold acting as the feature switch. ERSS
 * still forces the cursor back on per story in getCursorBehavior.
 *
 * Grade comes from userData.actualGrade, which upstream caps at 8 and sets to -1/0 for
 * the youngest students. A missing or non-numeric grade is NOT exempt: an unknown grade
 * falls back to the normal gates rather than opting a student in on a bad read, which
 * also keeps every existing caller that never sets actualGrade on its old path.
 *
 * Exported for ReaderManager's cursor_free_mode_enabled telemetry, so the log and the
 * decision cannot disagree about who took the shortcut.
 */
export function isCursorFreeGradeBandExempt({ actualGrade } = getUserData()) {
  const grade = Number(actualGrade);
  return Number.isFinite(grade) && grade >= CURSOR_FREE_GRADE_BAND_MIN;
}

// ============ ASR mode ============

// The vocabulary of the `localAsrWarmupRoute` telemetry field, of which LOCAL and
// SERVER are also the two decisions that can actually be latched.
export const LOCAL_ASR_ROUTE = Object.freeze({
  LOCAL: 'local',
  SERVER: 'server',
  // REPORT-ONLY, and never a latched decision. An undecided session is represented
  // by the ABSENCE of a decision (null) — that is what makes shouldUseLocalAsr fall
  // through to eligibility, and setLocalAsrWarmupRouteDecision is only ever called
  // with LOCAL or SERVER. This value exists so the log site does not spell the
  // fourth state as a bare string: placement_manager reports it for a measured
  // session whose run has not settled yet, and a fleet query filtering that field
  // needs the producer and itself to agree on one spelling of all four states
  // (absent / undecided / local / server).
  UNDECIDED: 'undecided',
});

// The session's capability routing decision, derived from the measured warmup
// verdict (TT-1169). Null while undecided, then { route, verdict } for the rest of
// the session. asr/localAsrWarmup owns producing it — it runs the warmup, knows who
// cancelled an unsettled run, and pushes the settled answer here — while this
// module owns ANSWERING with it, because shouldUseLocalAsr() is where every
// consumer already asks the server-vs-local question.
//
// Deliberately module state OUTSIDE `state`, untouched by initCursorSession: its
// lifecycle is the ASR runtime's, not the cursor session's. The runtime survives
// activity transitions and its measurement with it, so the decision resets only
// when SpeechStream disposes the runtime, which reaches this module through
// asr/localAsrWarmup's resetLocalAsrWarmup — a
// fresh runtime earns a fresh measurement and a fresh decision. Letting a
// ReaderManager remount clear it would discard a decision whose measured basis
// is still running in the worker.
//
// UNDECIDED MEANS STATUS QUO, NOT SERVER. While null, shouldUseLocalAsr answers
// from eligibility exactly as it did before this gate existed. The safe-looking
// alternative (no verdict → server) would take local ASR away from every session
// that merely lost its measurement to a race, which is most of the absent-verdict
// population; the measured-slow devices the gate exists for get a verdict and are
// gated on it.
let localAsrWarmupRouteDecision = null;

// Whether the in-flight capability run has outlived the story-launch deadline without
// settling (TT-1218). Pushed here by asr/localAsrWarmup from the deadline timer, for the same
// division of labour as the decision above: it knows when its own timer fired, this
// module answers routing questions with it.
//
// A SEPARATE FIELD, NOT A PROVISIONAL DECISION. Modelling this as
// { route: SERVER, provisional: true } would be read as SERVER by everything that
// already checks the decision — including shouldDownloadStoryLmGraphs, which would stop
// fetching the .fst graphs a FAST settle still needs, leaving that session routed local
// with nothing to decode against. The two states have to stay tellable apart, which is
// what the pair of questions below is for.
let localAsrWarmupDeadlineOverrun = false;

/**
 * Latches the warmup-derived routing decision for the session. Called by
 * asr/localAsrWarmup when the capability run settles with an answer; see
 * applyLocalAsrWarmupRouteDecision in that module for what every verdict means and why.
 * @param {{route: string, verdict: string}} decision One of LOCAL_ASR_ROUTE plus
 *   the verdict that produced it, kept for telemetry beside the enforcement.
 * @returns {void}
 */
export function setLocalAsrWarmupRouteDecision({ route, verdict } = {}) {
  const effectiveRoute = (FORCE_LOCAL_ASR_WARMUP && route === LOCAL_ASR_ROUTE.SERVER)
    ? LOCAL_ASR_ROUTE.LOCAL
    : route;
  localAsrWarmupRouteDecision = { route: effectiveRoute, verdict };
}

/**
 * The current warmup routing decision, or null while the session is unmeasured.
 * @returns {{route: string, verdict: string}|null}
 */
export function getLocalAsrWarmupRouteDecision() {
  return localAsrWarmupRouteDecision;
}

/**
 * Records that the story-launch deadline fired on a run that has not settled, so the
 * next phrase routes to the server provisionally (TT-1218). Called by asr/localAsrWarmup
 * from the deadline callback, generation-guarded there so a run whose runtime was disposed
 * mid-wait cannot leave this set for the session that replaced it.
 *
 * Not cleared when the run settles: the decision arriving is what stops this mattering,
 * because every question below consults the decision first. Cleared only with the
 * runtime, by resetLocalAsrWarmupRouting.
 * @returns {void}
 */
export function setLocalAsrWarmupDeadlineOverrun() {
  localAsrWarmupDeadlineOverrun = true;
}

/**
 * Releases the overrun because the run that owned it has SETTLED — a settled run is by
 * definition no longer in flight past its deadline. Called by
 * applyLocalAsrWarmupRouteDecision for a run that passed its staleness checks.
 *
 * Behaviour-neutral today, and deliberately so. Where the settle latches a decision, the
 * decision-first ordering in isLocalAsrProvisionallyServerRouted had already made this
 * flag inert; where it latches none, the flag was already false, because every
 * settle-without-decision path in that function requires the deadline NOT to have fired.
 *
 * It exists so that last clause stops being something you have to verify by reading three
 * branches. The flag is otherwise released only by a decision or by dispose, so a future
 * branch that returns without deciding while the deadline HAS fired would server-route
 * every phrase for the rest of the runtime, with one phraseWarmupGated per activity as the
 * only symptom — which reads as the gate working. Clearing on settle makes "an unsettled
 * run is the only thing that can hold this flag" true by construction.
 * @returns {void}
 */
export function clearLocalAsrWarmupDeadlineOverrun() {
  localAsrWarmupDeadlineOverrun = false;
}

/**
 * Clears all warmup-derived routing state alongside the runtime that produced it.
 *
 * One caller, reached indirectly: disposeLocalPlacementAsr calls
 * asr/localAsrWarmup's resetLocalAsrWarmup, which releases the session latch and
 * calls this in one step (TT-1231). Those two halves reset together or not at all,
 * which is why they are one function there rather than two calls here.
 *
 * One function rather than one per field on purpose: the decision and the overrun flag
 * describe the same measurement, and a teardown that reset one without the other would
 * either let a stale overrun route a runtime it never measured to the server, or leave a
 * verdict gating a runtime that no longer exists. They reset together or not at all.
 *
 * Dispose is the only caller even though initLocalPlacementAsr ALSO bumps the runtime
 * generation. That relies on an invariant held elsewhere: init early-returns on a ready
 * runtime, localAsrInitializing guards re-entry, and a handler's readiness only drops in
 * its constructor, on init failure, or in dispose — so a ready runtime cannot be replaced
 * without a dispose, and every generation bump that matters here goes through one. If that
 * stops being true, init needs this call too, and more urgently than it would have before
 * TT-1218: a stale decision is at least a real measurement of the same device, where a
 * stale overrun is the absence of one and is released only by a decision that a spent
 * latch guarantees will never arrive.
 * @returns {void}
 */
export function resetLocalAsrWarmupRouting() {
  localAsrWarmupRouteDecision = null;
  localAsrWarmupDeadlineOverrun = false;
}

/**
 * Whether the capability run has overrun its deadline WITHOUT settling (TT-1218) — the
 * state that makes shouldUseLocalAsrForNextPhrase answer server provisionally.
 *
 * SCOPE: this answers only "is there an unsettled overrun". It does NOT consider locale
 * or eligibility, so on an ineligible session it can be true for a phrase local ASR was
 * never a candidate for. Callers own the eligibility question. Named for the state rather
 * than for a route for that reason — shouldUseLocalAsrForNextPhrase is the one that folds
 * eligibility in and therefore the one that actually answers where a phrase is going.
 *
 * (The reachable consequence is a telemetry mislabel, not a misroute: an ineligible phrase
 * was going to the server regardless, but SpeechStream's gate reports it as
 * warmup-gated rather than ineligible. The settled-decision gate beside it has the same
 * shape, so the two agree with each other; making either defer to eligibility would
 * change what TT-1169 reports and wants deciding for both at once.)
 *
 * The absence of a decision is the load-bearing half. The overrun flag is never cleared
 * on settle, so without it a session that overran once would report a provisional route
 * for the rest of the session, and nothing could tell that state from a settled one.
 * Consulting the decision first is also what makes the pessimism provisional rather than
 * a latch: a run that settles FAST returns the session to local routing.
 *
 * Three call sites: shouldUseLocalAsrForNextPhrase below, SpeechStream's phrase gate
 * (which needs to distinguish WHY a phrase went to the server, because it reports the two
 * cases differently), and the placement_manager checkpoint, which reports it so a
 * provisionally-gated session is not read as merely undecided. Anything that only needs
 * to know WHERE the phrase is going wants shouldUseLocalAsrForNextPhrase instead.
 * @returns {boolean}
 */
export function isLocalAsrProvisionallyServerRouted() {
  return localAsrWarmupDeadlineOverrun && !localAsrWarmupRouteDecision;
}

/**
 * Whether local (on-device) ASR is in play for this SESSION — the durable question.
 * Defaults to the signed-in user's locale; callers with a session-specific locale
 * (e.g. Spanish activities) must pass it.
 *
 * Two gates, in order: the measured warmup verdict (a session judged unable to
 * keep pace answers server no matter how eligible it looks), then static
 * eligibility. A LOCAL decision does not shortcut eligibility — the verdict says
 * the device CAN decode fast enough, not that this locale/session may.
 *
 * ONE OF A PAIR — pick deliberately (TT-1218):
 *
 *   shouldUseLocalAsr             durable. "Could this session decode locally at all?"
 *                                 Answers from eligibility and the SETTLED verdict only,
 *                                 so it is stable across a story and safe to spend work
 *                                 on: per-story .fst downloads
 *                                 (shouldDownloadStoryLmGraphs), the decision to run a
 *                                 warmup measurement at all, the placement_manager label.
 *                                 A transient answer here would cancel work the session
 *                                 still needs — an overrun run that then settles FAST
 *                                 would find itself routed local with no graphs.
 *
 *   shouldUseLocalAsrForNextPhrase transient. "Where is the NEXT PHRASE's audio going?"
 *                                 Everything above plus the provisional overrun route,
 *                                 so it can change between phrases. What the server-ASR
 *                                 connection waits and kickstart must use: they exist to
 *                                 make sure Kaldi is ready before the student reads, and
 *                                 the question they are really asking is whether this
 *                                 phrase is Kaldi's.
 *
 * Rule of thumb: if getting it wrong wastes work, ask the durable one; if getting it
 * wrong loses audio, ask the transient one.
 */
export function shouldUseLocalAsr({ locale, storyLocale } = {}) {
  if (localAsrWarmupRouteDecision
    && localAsrWarmupRouteDecision.route === LOCAL_ASR_ROUTE.SERVER) {
    return false;
  }
  const resolved = locale || resolveLocalAsrPlacementLocale({ storyLocale });
  return shouldUseLocalPlacementAsr({ locale: resolved });
}

/**
 * Whether the NEXT PHRASE's placement audio goes to local ASR — the transient question.
 * See shouldUseLocalAsr for which of the pair to ask and why they differ.
 *
 * Answers false for a session that is eligible, unmeasured, and past its warmup deadline:
 * SpeechStream's phrase gate routes that phrase to the server, and a consumer that
 * disagreed would skip the Kaldi connection wait for a phrase Kaldi is about to be asked
 * to decode — on the slowest hardware in the fleet, which is the population TT-1218
 * exists for. Returns to true from the next phrase if the run settles FAST.
 * @param {object} [options]
 * @param {string} [options.locale]
 * @returns {boolean}
 */
export function shouldUseLocalAsrForNextPhrase({ locale, storyLocale } = {}) {
  if (isLocalAsrProvisionallyServerRouted()) {
    return false;
  }
  return shouldUseLocalAsr({ locale, storyLocale });
}

/**
 * Whether the session should download the story's per-phrase LM decode graphs
 * (biased HCLG .fst files) at story selection: local ASR is active for this session
 * AND the vosk backend is selected (NeMo biases from the word list in memory and has
 * no per-story assets). ReaderManager calls this after a story is fetched, before
 * Ready-Set-Go, and shows the download progress UI while the graphs prime.
 */
export function shouldDownloadStoryLmGraphs({ locale, storyLocale } = {}) {
  return shouldUseLocalAsr({ locale, storyLocale }) && getLocalAsrBackendId() === LOCAL_ASR_BACKEND.Vosk;
}

// ============ FTUE coordination ============

/**
 * Checks if the FTUE for a cursor behavior has been completed.
 * Behaviors without an FTUE have nothing to complete, so they return true.
 */
export function hasCursorBehaviorFTUECompleted(behavior = getCursorBehavior()) {
  const ftueId = BEHAVIOR_FTUE_IDS[behavior];
  if (!ftueId) {
    return true;
  }
  if (FTUEService.isFTUECompleted(ftueId)) {
    return true;
  }
  // A bad-signal placement (TT-991) records its onboarding under its own id -- see
  // BAD_SIGNAL_ONBOARDING_FTUE_IDS -- and, for the rest of this page load, in memory, so a forced
  // session that persists nothing still sees the interstitial once rather than once per story.
  if (badSignalAppliedBehavior === behavior) {
    return badSignalOnboardingSeen.has(behavior)
      || FTUEService.isFTUECompleted(BAD_SIGNAL_ONBOARDING_FTUE_IDS[behavior]);
  }
  return false;
}

/**
 * Marks the FTUE for a cursor behavior as completed (persisted to the user record).
 */
export async function markCursorBehaviorFTUEComplete(behavior) {
  const ftueId = BEHAVIOR_FTUE_IDS[behavior];
  if (!ftueId) {
    return;
  }

  // TT-991: a placement the bad-signal branch APPLIED records its onboarding under the branch's
  // own id, never under the legacy one. Provenance, not inference -- badSignalAppliedBehavior is set
  // only where computeEligibleBehavior returns the branch's answer, so a student the LEGACY rules
  // moved (including in a log-only district, where the branch's answer is withheld) still gets the
  // legacy write below, byte for byte what it was.
  //
  // Why not the legacy flag: 'cursor-free-tutor' is what bypassWcpmCheck reads as "keep this
  // student in cursor-free regardless of WCPM". Written here, it would keep a bad-signal student in
  // cursor-free through the legacy path after a CSM cleared badSignalAltCursorMode -- the documented
  // manual exit would do nothing. With the branch's own id, nulling badSignalAltCursorMode is a
  // complete exit.
  //
  // A forced test session (TT-991's own URL params) persists nothing at all, so the tester's account
  // is left exactly as found; the in-memory record still stops the interstitial re-showing on every
  // later story of that session.
  if (badSignalAppliedBehavior === behavior) {
    badSignalOnboardingSeen.add(behavior);
    if (isBadSignalForcedSession()) {
      return;
    }
    return FTUEService.setFTUEStatus(BAD_SIGNAL_ONBOARDING_FTUE_IDS[behavior], true);
  }

  return FTUEService.setFTUEStatus(ftueId, true);
}

/**
 * Whether the current behavior's FTUE should be shown before launching a story.
 * Decided once at initCursorSession; returns false while an ERSS story is active
 * (behavior falls back to STANDARD) but the flag survives for the next story.
 */
export function shouldShowCursorBehaviorFTUE() {
  return state.shouldShowFTUE && Boolean(BEHAVIOR_FTUE_IDS[getCursorBehavior()]);
}

export function clearShouldShowCursorBehaviorFTUE() {
  state.shouldShowFTUE = false;
}

export function getCursorBehaviorFTUEVariant() {
  return state.ftueVariant;
}

function computeCursorFreeFTUEVariant() {
  const metrics = getUserData().studentMetrics || { storiesRead: 0, testsTaken: 0 };
  // Students placed above threshold via an assessment have never used the tutor
  // cursor, so their FTUE variant skips the cursor demonstration
  return metrics.testsTaken > 0 && metrics.storiesRead < 1
    ? CURSOR_FTUE_VARIANTS.POST_ASSESSMENT
    : CURSOR_FTUE_VARIANTS.STANDARD;
}

export default {
  CURSOR_BEHAVIOR,
  CURSOR_FTUE_VARIANTS,
  LOCAL_ASR_ROUTE,
  initCursorSession,
  setStoryContext,
  getCursorBehavior,
  isCursorFreeBehavior,
  isTapToReadBehavior,
  computeEligibleBehavior,
  getBadSignalDecision,
  getBadSignalNotEvaluatedReason,
  isCursorFreeGradeBandExempt,
  shouldUseLocalAsr,
  shouldUseLocalAsrForNextPhrase,
  shouldDownloadStoryLmGraphs,
  setLocalAsrWarmupRouteDecision,
  getLocalAsrWarmupRouteDecision,
  setLocalAsrWarmupDeadlineOverrun,
  resetLocalAsrWarmupRouting,
  isLocalAsrProvisionallyServerRouted,
  hasCursorBehaviorFTUECompleted,
  markCursorBehaviorFTUEComplete,
  shouldShowCursorBehaviorFTUE,
  clearShouldShowCursorBehaviorFTUE,
  getCursorBehaviorFTUEVariant,
  resetCursorOrchestratorForTests,
};
